import { useQuery, useQueries, useMutation, useQueryClient } from 'react-query';
import { useOktaAuth } from '@okta/okta-react';

const defaultCacheTime = 1000 * 60 * 30;

const useOktaQuery = (queryKey, queryFn, queryOpts) => {
    const { authState } = useOktaAuth();
    const opts = { retry: window.$IsDugoutApp ? false : 3, ...queryOpts };
    if (window.$IsDugoutApp) opts.retry = false;
    opts.enabled = !!authState?.accessToken && (queryOpts?.enabled ?? true);
    const useQueryResp = useQuery(queryKey, () => queryFn(authState.accessToken), opts);
    return Object.assign(useQueryResp, { authState });
};

export const usePrefetchOktaQuery = async (queryKey, queryFn, queryOpts, timeoutMillis) => {
    const timeout = timeoutMillis ?? defaultCacheTime;
    const { authState } = useOktaAuth();
    const opts = { staleTime: timeout, cacheTime: timeout, ...queryOpts, retry: false };
    const queryClient = useQueryClient();
    if (!authState?.accessToken) return;
    await queryClient.prefetchQuery(queryKey, () => queryFn(authState.accessToken), opts);
};

export const useCachedOktaQuery = (queryKey, queryFn, queryOpts, timeoutMillis) => {
    const timeout = timeoutMillis ?? defaultCacheTime;
    const { authState } = useOktaAuth();
    const opts = { staleTime: timeout, cacheTime: timeout, retry: 3, ...queryOpts };
    if (window.$IsDugoutApp) opts.retry = false;
    opts.enabled = !!authState?.accessToken && (queryOpts?.enabled ?? true);
    const useQueryResp = useQuery(queryKey, () => queryFn(authState.accessToken), opts);
    return Object.assign(useQueryResp, { authState });
};

export const useOktaQueries = (queries) => {
    const { authState } = useOktaAuth();
    return useQueries(queries.map((q) => ({
        retry: window.$IsDugoutApp ? false : 3,
        ...q,
        enabled: !!authState?.accessToken && (q.enabled ?? true),
        queryFn: () => q.queryFn(authState.accessToken),
    })));
};

export const useOktaMutation = (mutationFn, mutationOpts) => {
    const { authState } = useOktaAuth();
    const mutation = useMutation((...variables) => mutationFn(authState.accessToken, ...variables), mutationOpts);
    return Object.assign(mutation, { authState });
};

export default useOktaQuery;
